import {
  createRoomCode,
  isRoomCode,
  normalizeRoomCode,
  type RemoteState,
} from '../core/remote-room';
import {
  PollingRemoteTransport,
  type RemoteStatus,
  type RemoteTransport,
} from '../core/remote-transport';
import {element, button} from './dom';

const STATUS_TEXT: Record<RemoteStatus, string> = {
  local: 'Local',
  connecting: 'Connecting…',
  connected: 'Connected',
  error: 'No connection',
};

export type RemoteControlOptions = {
  deckId: string;
  room: string | null;
  role: 'host' | 'join';
  /** What this view would publish right now. */
  currentState: () => RemoteState;
  /** Put the view where the other device says it is. */
  applyState: (state: RemoteState) => void;
  onRoomChange: (room: string | null) => void;
  /** Present on the host, where a link to the joining view can be copied. */
  peerUrl?: (room: string) => string;
  createTransport?: (deckId: string, room: string) => RemoteTransport;
};

/**
 * Opt-in remote control, off unless a room is in the URL.
 *
 * Both views publish their own position and follow the other's: the laptop's
 * keyboard and the phone's buttons are the same kind of event, and whichever
 * happened last is where the deck is. The server decides "last" with a
 * revision, so two people pressing at once cannot leave the two screens
 * disagreeing.
 */
export class RemoteControl {
  readonly element: HTMLElement;
  #options: RemoteControlOptions;
  #transport: RemoteTransport | null = null;
  #applying = false;
  #status: HTMLElement;
  #code: HTMLElement;
  #presence: HTMLElement;
  #room: string | null = null;
  #primary: HTMLButtonElement;
  #joinForm: HTMLFormElement;
  #joinInput: HTMLInputElement;
  #copy: HTMLButtonElement;
  #leave: HTMLButtonElement;

  constructor(options: RemoteControlOptions) {
    this.#options = options;
    this.element = element('div', 'remote');
    this.#presence = element('span', 'remote-presence');
    this.#status = element('span', 'remote-status');
    this.#code = element('span', 'remote-code');
    this.#presence.append(this.#status, this.#code);
    this.#primary = button(
      options.role === 'host' ? 'Remote' : 'Connect',
      'button button-quiet remote-primary',
    );
    this.#primary.addEventListener('click', () => {
      if (options.role === 'host') this.start(createRoomCode());
      else {
        this.#joinForm.hidden = false;
        this.#primary.hidden = true;
        this.#joinInput.focus();
      }
    });
    this.#joinForm = document.createElement('form');
    this.#joinForm.className = 'remote-join';
    this.#joinForm.hidden = true;
    this.#joinForm.ariaLabel = 'Connect to remote control';
    this.#joinInput = document.createElement('input');
    this.#joinInput.className = 'remote-input';
    this.#joinInput.name = 'room';
    this.#joinInput.placeholder = 'Room code';
    this.#joinInput.ariaLabel = 'Room code';
    this.#joinInput.autocomplete = 'off';
    this.#joinInput.autocapitalize = 'characters';
    this.#joinInput.spellcheck = false;
    this.#joinInput.maxLength = 8;
    this.#joinInput.addEventListener('input', () => {
      this.#joinInput.value = normalizeRoomCode(this.#joinInput.value).slice(0, 6);
      this.#joinInput.setCustomValidity('');
    });
    const submit = button('Connect', 'button button-primary');
    submit.type = 'submit';
    this.#joinForm.append(this.#joinInput, submit);
    this.#joinForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const room = normalizeRoomCode(this.#joinInput.value);
      if (!isRoomCode(room)) {
        this.#joinInput.setCustomValidity('Enter the six-character room code.');
        this.#joinInput.reportValidity();
        return;
      }
      this.start(room);
    });
    this.#copy = button('Copy link', 'button button-quiet remote-copy');
    this.#copy.ariaLabel = 'Copy speaker link';
    this.#copy.hidden = true;
    this.#copy.addEventListener('click', () => void this.copyPeerLink());
    this.#leave = button('Disconnect', 'button button-quiet remote-leave');
    this.#leave.hidden = true;
    this.#leave.addEventListener('click', () => this.leave());
    this.element.append(
      this.#presence,
      this.#primary,
      this.#joinForm,
      this.#copy,
      this.#leave,
    );
    this.render('local', '');
    const initialRoom = normalizeRoomCode(options.room);
    if (isRoomCode(initialRoom)) this.start(initialRoom, {silent: true});
  }

  /** True while a remote state is being applied, so the view does not echo it back. */
  get applying(): boolean {
    return this.#applying;
  }

  publishLocal(): void {
    if (this.#applying) return;
    this.#transport?.publish(this.#options.currentState());
  }

  close(): void {
    this.#transport?.close();
    this.#transport = null;
  }

  private start(room: string, {silent = false}: {silent?: boolean} = {}): void {
    this.close();
    this.#room = room;
    this.#joinForm.hidden = true;
    this.#joinInput.value = '';
    const create =
      this.#options.createTransport ??
      ((deckId: string, code: string) => new PollingRemoteTransport({deckId, room: code}));
    this.#transport = create(this.#options.deckId, room);
    this.#transport.onStatus((status, detail) => this.render(status, detail, room));
    this.#transport.onState((state) => {
      this.#applying = true;
      try {
        this.#options.applyState(state);
      } finally {
        this.#applying = false;
      }
    });
    this.#options.onRoomChange(room);
    if (!silent) this.#transport.publish(this.#options.currentState());
  }

  private leave(): void {
    this.close();
    this.#room = null;
    this.#joinForm.hidden = true;
    this.#joinInput.value = '';
    this.#options.onRoomChange(null);
    this.render('local', '');
  }

  private async copyPeerLink(): Promise<void> {
    if (!this.#room || !this.#options.peerUrl) return;
    const url = new URL(this.#options.peerUrl(this.#room), location.href).href;
    try {
      await navigator.clipboard.writeText(url);
      this.#copy.textContent = 'Link copied';
      window.setTimeout(() => {
        this.#copy.textContent = 'Copy link';
      }, 1_600);
    } catch (cause) {
      this.#copy.textContent = 'Copy failed';
      this.#copy.title = cause instanceof Error ? cause.message : 'Copy failed';
    }
  }

  private render(status: RemoteStatus, detail: string, room?: string): void {
    this.#status.textContent = STATUS_TEXT[status];
    this.#status.dataset.status = status;
    this.#status.title = detail;
    const activeRoom = room ?? this.#room;
    this.#code.textContent = activeRoom ?? '';
    this.#primary.hidden = Boolean(activeRoom) || !this.#joinForm.hidden;
    this.#copy.hidden = !activeRoom || !this.#options.peerUrl;
    this.#leave.hidden = !activeRoom;
  }
}
